import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { IonPage } from '@ionic/react';
import { useIonRouter } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { OUTBOX_OPEN_PARAM, returnThroughOutboxLeg } from '@/modules/outbox';
import { loadPersistedWizardState } from '@/modules/share/session_storage';
import { MePrFoService } from '@/services/meprfo';
import { identifyWithPendingInvite } from '@/services/iam';
import { decideLegTap } from './useImpliedConfirm';

/**
 * Handles the shareable leg link `/b/{legId}`.
 *
 * The server no longer 302-redirects this path: it serves the React shell with
 * OpenGraph preview tags (so the link unfurls with the action image), and this
 * component resolves the leg and routes the visitor client-side:
 *
 *  1. legId is the message the outbox is waiting on ("did it send?") → the
 *     sender returned from SMS on it: `returnThroughOutboxLeg` confirms it,
 *     and the topic opens with the outbox over it.
 *  2. legId = the share wizard's in-progress leg (implied confirm) → the
 *     sender returned from SMS: route through ShareWizardRedirect
 *     (`/topics/{topicId}/share`) which then navigates to the correct confirm
 *     step.
 *  3. Otherwise a recipient tapped the link: count the click-through and send
 *     them to the action view `/topics/{topicId}?legId={legId}`.
 *
 * Uses window.location.replace (hard navigation) rather than React Router
 * <Redirect> because Ionic's IonRouterOutlet can't reliably animate a
 * cross-route transition from /b → /topics when starting from a cold load.
 * A hard navigation matches the original backend-302 behavior.
 *
 * Like ShareWizardRedirect, this runs outside ShareWizardProvider, so it reads
 * the in-progress leg from persisted storage rather than context.
 */
const LegRedirect: React.FC = () => {
  const { legId } = useParams<{ legId: string }>();
  const router = useIonRouter();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    // Read the query string from Ionic's routeInfo first, falling back to the
    // document URL on a hard load — matching ShareWizardRedirect/useImpliedConfirm.
    const rawSearch = router.routeInfo?.search || location.search || '';
    const params = new URLSearchParams(rawSearch.replace(/^\?/, ''));

    const resolveThroughWizardOrRecipient = () =>
      MePrFoService.lookupLegTopicId(legId)
        .then(topicId => {
          if (cancelled) return;
          if (!topicId) {
            window.location.replace('/');
            return;
          }
          loadPersistedWizardState(topicId).then(persisted => {
            if (cancelled) return;
            const { isImpliedConfirm } = decideLegTap(legId, persisted);

            if (isImpliedConfirm) {
              // Sender returning from SMS — route through ShareWizardRedirect
              // so the per-step routing (share-confirm vs final-share-confirm)
              // is decided once within the TopicRouter context.
              // `f` is the sender's own display name — pointless on their return.
              params.delete('f');
              params.set('legId', legId);
              const base = `/topics/${encodeURIComponent(topicId)}/share`;
              window.location.replace(`${base}?${params.toString()}`);
            } else {
              // Recipient tapped the link — count the click-through, then show
              // the action view, whatever the topic's type. Keep `f` so it can
              // credit who shared it, and forward `legId` so the type's welcome
              // sheet can tell the story that traveled with this leg (news reads
              // its allowed detail, reshare story and goal progress from it). The
              // action view itself reads the PendingInvite capability from the
              // JWT claim, not the URL.
              MePrFoService.recordLegClickThru(legId).catch(() => {});
              params.set('legId', legId);
              const target = `/topics/${encodeURIComponent(topicId)}?${params.toString()}`;
              // Stamp a pending invite on the session so a recipient who isn't an
              // invitee yet can be offered an "unlock by linking your phone" path on
              // a by-invitation-only action. Best-effort and awaited so the cookie
              // is in place before the hard navigation; navigate regardless.
              identifyWithPendingInvite(legId).finally(() => {
                if (!cancelled) window.location.replace(target);
              });
            }
          });
        })
        .catch(() => {
          if (!cancelled) window.location.replace('/');
        });

    returnThroughOutboxLeg(legId)
      .catch(() => null)
      .then(outboxTopicId => {
        if (cancelled) return;
        if (outboxTopicId) {
          const target = `/topics/${encodeURIComponent(outboxTopicId)}`;
          const query = new URLSearchParams({
            [OUTBOX_OPEN_PARAM]: outboxTopicId,
          });
          window.location.replace(`${target}?${query.toString()}`);
          return;
        }
        resolveThroughWizardOrRecipient();
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legId]); // intentionally omits search — URL is stable at redirect time

  // Render an IonPage so Ionic's page stack has a valid entry while the async
  // resolution runs. Without this, the stack has a gap that can cause warnings.
  return <IonPage />;
};

export default LegRedirect;
